import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Divider,
  TextField,
  Alert,
  Snackbar,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PeopleIcon from '@mui/icons-material/People';
import HistoryIcon from '@mui/icons-material/History';
import { useEvents } from '../../contexts/EventContext';
import { useAuth } from '../../contexts/AuthContext';
import { formatDate, formatTime, getSpotsRemaining, getCapacityPercentage, isRegistrationOpen, isDateInPast } from '../../utils/helpers';
import { getCategoryLabel, getCategoryColor, FamilyMember, RELATIONSHIP_LABELS } from '../../types';
import { ROUTES } from '../../utils/constants';
import api from '../../services/api';
import FeedbackForm from '../../components/events/FeedbackForm';
import GuestPickerRow from '../../components/guests/GuestPickerRow';
import GuestForm from '../../components/guests/GuestForm';
import DuplicateGuestDialog from '../../components/guests/DuplicateGuestDialog';
import { listGuests, deleteGuest } from '../../services/guestsApi';
import { visibleAttendees } from '../../utils/attendee';
import type { EventGuest } from '../../types/eventGuest';

const EventDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { getEvent, registerForEvent, getUserRegistrations } = useEvents();
  const { user, isAuthenticated } = useAuth();

  // Determine back navigation based on where user came from
  const fromMyRegistrations = (location.state as { from?: string } | null)?.from === 'my-registrations';

  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [specialRequirements, setSpecialRequirements] = useState('');
  // C7 — per-attendee dietary edits (keyed by member/guest id). Pre-filled from
  // the person's saved value; only touched/non-empty entries are sent.
  const [dietaryEdits, setDietaryEdits] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Plan 4.11 — Event Guest state
  const familyId =
    user?.familyMembers?.find((m) => m.crmFamilyId)?.crmFamilyId ?? null;
  const [guests, setGuests] = useState<EventGuest[]>([]);
  const [selectedGuestIds, setSelectedGuestIds] = useState<string[]>([]);
  const [showGuestForm, setShowGuestForm] = useState(false);
  const [duplicateContext, setDuplicateContext] = useState<{
    newGuest: EventGuest;
    existingGuestId: string;
  } | null>(null);

  // Fetch family members when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      // Try to get from user object first, otherwise fetch
      if (user.familyMembers && user.familyMembers.length > 0) {
        setFamilyMembers(user.familyMembers);
      } else {
        setLoadingMembers(true);
        api.get('/users/profile')
          .then((res) => {
            setFamilyMembers(res.data.familyMembers || []);
          })
          .catch(() => {
            setFamilyMembers([]);
          })
          .finally(() => {
            setLoadingMembers(false);
          });
      }
    }
  }, [isAuthenticated, user]);

  // Plan 4.11 — Load EventGuests for the user's family. Best-effort:
  // backend 404s when the feature flag is off (anti-enumeration), in
  // which case we simply render no guest UI.
  useEffect(() => {
    if (!isAuthenticated || !familyId) return;
    listGuests(familyId)
      .then((rs) => setGuests(rs.filter((g) => !g.isRedacted)))
      .catch(() => setGuests([]));
  }, [isAuthenticated, familyId]);

  const event = id ? getEvent(id) : undefined;

  if (!event) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h5" gutterBottom>
          Event not found
        </Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(fromMyRegistrations ? ROUTES.MY_REGISTRATIONS : ROUTES.EVENTS)}>
          {fromMyRegistrations ? 'Back to My Registrations' : 'Back to Events'}
        </Button>
      </Box>
    );
  }

  const spotsRemaining = getSpotsRemaining(event.capacity, event.currentAttendees);
  const capacityPercentage = getCapacityPercentage(event.capacity, event.currentAttendees);
  const canRegister = isRegistrationOpen(event.registrationDeadline, event.registrationDeadlineTime) && spotsRemaining > 0;

  // Check if event is in the past (compare dates only, not time)
  const isPastEvent = isDateInPast(event.date);

  // Check if user is already registered
  const userRegistrations = user ? getUserRegistrations(user.id) : [];
  const existingRegistration = userRegistrations.find(
    (r) => r.eventId === event.id && r.status === 'confirmed'
  );

  // Check if user was registered for a past event (can leave feedback)
  const canLeaveFeedback = isPastEvent && existingRegistration;

  const handleToggleMember = (memberId: string) => {
    setSelectedMemberIds((prev) =>
      prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId]
    );
  };

  // De-duplicated, angel-free attendee list (one row per living person).
  const attendees = visibleAttendees(familyMembers);

  const totalSelected = selectedMemberIds.length + selectedGuestIds.length;

  const handleToggleGuest = (guestId: string) => {
    setSelectedGuestIds((prev) =>
      prev.includes(guestId) ? prev.filter((id) => id !== guestId) : [...prev, guestId],
    );
  };

  const handleRegister = async () => {
    if (!user) {
      navigate(ROUTES.LOGIN);
      return;
    }

    // Plan 4.11 — sum family members + guests for the empty / over-capacity checks.
    if (totalSelected === 0) {
      setError('Please select at least one attendee');
      return;
    }

    if (totalSelected > spotsRemaining) {
      setError(`Only ${spotsRemaining} spots available`);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // C7 — build the per-attendee dietary map. Include an entry when the user
      // touched the field (so an edit-to-clear is honoured) or the person has a
      // saved value (so the CRM register snapshot carries it).
      const dietaryByAttendee: Record<string, string> = {};
      const addDietary = (id: string, stored?: string | null) => {
        const touched = id in dietaryEdits;
        const value = touched ? dietaryEdits[id] : (stored ?? '');
        if (touched || value.trim().length > 0) dietaryByAttendee[id] = value;
      };
      selectedMemberIds.forEach((id) =>
        addDietary(id, familyMembers.find((m) => m.id === id)?.dietaryRequirements),
      );
      selectedGuestIds.forEach((id) =>
        addDietary(id, guests.find((g) => g.id === id)?.dietaryRequirements),
      );

      await registerForEvent(
        {
          eventId: event.id,
          familyMemberIds: selectedMemberIds,
          guestIds: selectedGuestIds,
          specialRequirements: specialRequirements || undefined,
          dietaryByAttendee: Object.keys(dietaryByAttendee).length ? dietaryByAttendee : undefined,
        },
        user.id
      );
      setShowSuccess(true);
      setTimeout(() => {
        navigate(ROUTES.MY_REGISTRATIONS);
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(fromMyRegistrations ? ROUTES.MY_REGISTRATIONS : ROUTES.EVENTS)}
        sx={{ mb: 3 }}
      >
        {fromMyRegistrations ? 'Back to My Registrations' : 'Back to Events'}
      </Button>

      <Grid container spacing={4}>
        {/* Event Details */}
        <Grid item xs={12} md={8}>
          <Box
            sx={{
              height: 200,
              backgroundColor: getCategoryColor(event.category),
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
            }}
          >
            <EventIcon sx={{ fontSize: 80, color: 'white' }} />
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Chip
              label={getCategoryLabel(event.category)}
              sx={{
                backgroundColor: getCategoryColor(event.category),
                color: 'white',
              }}
            />
            {spotsRemaining === 0 && <Chip label="FULL" color="error" />}
          </Box>

          <Typography variant="h4" fontWeight={600} gutterBottom>
            {event.title}
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EventIcon color="action" />
              <Typography>{formatDate(event.date)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTimeIcon color="action" />
              <Typography>
                {formatTime(event.startTime)} - {formatTime(event.endTime)}
              </Typography>
            </Box>
            {event.locationIsOnline ? (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <LocationOnIcon color="action" sx={{ mt: 0.25 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography>Online (Zoom)</Typography>
                  {existingRegistration?.meetingUrl?.startsWith('https://') ? (
                    <Button
                      variant="contained"
                      size="small"
                      component="a"
                      href={existingRegistration.meetingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      endIcon={<OpenInNewIcon />}
                      sx={{ mt: 0.5 }}
                    >
                      Join on Zoom
                    </Button>
                  ) : (
                    !existingRegistration && (
                      <Typography variant="caption" color="text.secondary" component="div">
                        The join link is sent when you register.
                      </Typography>
                    )
                  )}
                </Box>
              </Box>
            ) : event.locationKey && event.locationDisplayName ? (
              // Plan 4J: structured location. Render display name + multi-line
              // address; use the resolved locationMapUrl (set by CRM) when
              // present, else fall back to a postcode-based maps query.
              <Box
                component={event.locationMapUrl ? 'a' : 'div'}
                {...(event.locationMapUrl
                  ? {
                      href: event.locationMapUrl,
                      target: '_blank',
                      rel: 'noopener noreferrer',
                    }
                  : {})}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  color: 'inherit',
                  textDecoration: 'none',
                  '&:hover': event.locationMapUrl ? { color: '#1976d2' } : {},
                }}
              >
                <LocationOnIcon color="action" sx={{ mt: 0.25 }} />
                <Box>
                  <Typography>{event.locationDisplayName}</Typography>
                  {event.locationAddressLine1 && (
                    <Typography variant="caption" color="text.secondary" component="div">
                      {event.locationAddressLine1}
                      {event.locationAddressLine2 ? `, ${event.locationAddressLine2}` : ''}
                    </Typography>
                  )}
                  {(event.locationCity || event.locationPostcode) && (
                    <Typography variant="caption" color="text.secondary" component="div">
                      {[event.locationCity, event.locationPostcode].filter(Boolean).join(' ')}
                    </Typography>
                  )}
                </Box>
                {event.locationMapUrl && (
                  <OpenInNewIcon sx={{ fontSize: 16, color: 'action.active', mt: 0.5 }} />
                )}
              </Box>
            ) : (
              // Legacy free-text location (events created before Plan 4J).
              <Box
                component="a"
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: 'inherit',
                  textDecoration: 'none',
                  '&:hover': { color: '#1976d2' },
                }}
              >
                <LocationOnIcon color="action" />
                <Typography>{event.location}</Typography>
                <OpenInNewIcon sx={{ fontSize: 16, color: 'action.active' }} />
              </Box>
            )}
          </Box>

          <Typography variant="h6" fontWeight={600} gutterBottom>
            About this event
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            {event.description}
          </Typography>
        </Grid>

        {/* Registration Card / Feedback Section */}
        <Grid item xs={12} md={4}>
          {/* Past Event Banner */}
          {isPastEvent && (
            <Alert
              severity="info"
              icon={<HistoryIcon />}
              sx={{ mb: 2 }}
            >
              This event has ended.
            </Alert>
          )}

          {/* Feedback Form for past events */}
          {canLeaveFeedback && (
            <Box sx={{ mb: 2 }}>
              <FeedbackForm eventId={event.id} eventTitle={event.title} />
            </Box>
          )}

          <Card sx={{ position: 'sticky', top: 80 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {isPastEvent ? 'Event Summary' : 'Registration'}
              </Typography>

              {/* Capacity indicator */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PeopleIcon fontSize="small" />
                    <Typography variant="body2">Capacity</Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={500}>
                    {event.currentAttendees} / {event.capacity}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={capacityPercentage}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: '#e0e0e0',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor:
                        capacityPercentage >= 90
                          ? '#f44336'
                          : capacityPercentage >= 70
                          ? '#ff9800'
                          : '#4caf50',
                    },
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  {spotsRemaining} spots remaining
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Registration deadline */}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Registration deadline: {formatDate(event.registrationDeadline)}
              </Typography>

              {isPastEvent ? (
                existingRegistration ? (
                  <Alert severity="success">
                    You attended this event with {existingRegistration.attendeeCount} attendee(s).
                    {!canLeaveFeedback && ' Thank you for joining us!'}
                  </Alert>
                ) : (
                  <Alert severity="info">
                    This event has ended. We hope to see you at future events!
                  </Alert>
                )
              ) : existingRegistration ? (
                <Alert severity="success">
                  You are already registered for this event with{' '}
                  {existingRegistration.attendeeCount} attendee(s).
                </Alert>
              ) : !isAuthenticated ? (
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Please sign in to register for this event.
                  </Alert>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => navigate(ROUTES.LOGIN)}
                  >
                    Sign In to Register
                  </Button>
                </Box>
              ) : canRegister ? (
                <Box>
                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {error}
                    </Alert>
                  )}

                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Who will be attending?
                  </Typography>

                  {loadingMembers ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : attendees.length === 0 ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      No family members found on your account. Please contact Team Jak to update your family details before registering.
                    </Alert>
                  ) : (
                    <FormGroup sx={{ mb: 2 }}>
                      {attendees.map((member) => {
                        const isSelected = selectedMemberIds.includes(member.id);
                        return (
                          <Box key={member.id} sx={{ mb: isSelected ? 1 : 0 }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={isSelected}
                                  onChange={() => handleToggleMember(member.id)}
                                  disabled={!isSelected && totalSelected >= spotsRemaining}
                                />
                              }
                              label={`${member.name} (${RELATIONSHIP_LABELS[member.relationship] || member.relationship})`}
                            />
                            {/* C7 — dietary for each selected attendee, pre-filled + editable. */}
                            {isSelected && (
                              <TextField
                                fullWidth
                                size="small"
                                label="Dietary requirements (optional)"
                                value={dietaryEdits[member.id] ?? member.dietaryRequirements ?? ''}
                                inputProps={{ maxLength: 500 }}
                                onChange={(e) =>
                                  setDietaryEdits((prev) => ({ ...prev, [member.id]: e.target.value }))
                                }
                                sx={{ ml: 4, mb: 1, width: 'calc(100% - 32px)' }}
                              />
                            )}
                          </Box>
                        );
                      })}
                    </FormGroup>
                  )}

                  {/* Plan 4.11 — inline guest picker + add-guest button */}
                  {familyId && guests.length > 0 && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <GuestPickerRow
                        guests={guests}
                        selectedGuestIds={selectedGuestIds}
                        onToggle={handleToggleGuest}
                        remainingCapacity={spotsRemaining}
                        selectedFamilyMemberCount={selectedMemberIds.length}
                      />
                    </>
                  )}
                  {familyId && (
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => setShowGuestForm(true)}
                      size="small"
                      sx={{ mt: 1, mb: 1, alignSelf: 'flex-start' }}
                    >
                      Add a new guest
                    </Button>
                  )}

                  {totalSelected > 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {totalSelected} attendee{totalSelected !== 1 ? 's' : ''} selected
                    </Typography>
                  )}

                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Special Requirements (optional)"
                    placeholder="Dietary requirements, accessibility needs, etc."
                    value={specialRequirements}
                    onChange={(e) => setSpecialRequirements(e.target.value)}
                    sx={{ mb: 3 }}
                  />

                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={handleRegister}
                    disabled={isSubmitting || totalSelected === 0}
                  >
                    {isSubmitting ? 'Registering...' : 'Register Now'}
                  </Button>
                </Box>
              ) : (
                <Alert severity="warning">
                  {spotsRemaining === 0
                    ? 'This event is fully booked.'
                    : 'Registration for this event has closed.'}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={showSuccess}
        autoHideDuration={2000}
        onClose={() => setShowSuccess(false)}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          Successfully registered for the event!
        </Alert>
      </Snackbar>

      {/* Plan 4.11 — Add-guest form */}
      {showGuestForm && familyId && (
        <Dialog open onClose={() => setShowGuestForm(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 600 }}>Add a guest</DialogTitle>
          <DialogContent>
            <GuestForm
              mode="create"
              familyId={familyId}
              onCancel={() => setShowGuestForm(false)}
              onSuccess={(guest, warning) => {
                setShowGuestForm(false);
                if (warning && warning.existingGuestId) {
                  setDuplicateContext({
                    newGuest: guest,
                    existingGuestId: warning.existingGuestId,
                  });
                } else {
                  setGuests((prev) => [...prev, guest]);
                  setSelectedGuestIds((prev) => [...prev, guest.id]);
                }
              }}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Plan 4.11 — Possible-duplicate dialog */}
      {duplicateContext && familyId && (
        <DuplicateGuestDialog
          open
          newGuest={duplicateContext.newGuest}
          existingGuestId={duplicateContext.existingGuestId}
          existingGuests={guests}
          onAddAnyway={() => {
            setGuests((prev) => [...prev, duplicateContext.newGuest]);
            setSelectedGuestIds((prev) => [...prev, duplicateContext.newGuest.id]);
            setDuplicateContext(null);
          }}
          onUseExisting={() => {
            void deleteGuest(familyId, duplicateContext.newGuest.id);
            setSelectedGuestIds((prev) =>
              Array.from(new Set([...prev, duplicateContext.existingGuestId])),
            );
            setDuplicateContext(null);
          }}
          onCancel={() => {
            void deleteGuest(familyId, duplicateContext.newGuest.id);
            setDuplicateContext(null);
          }}
        />
      )}
    </Box>
  );
};

export default EventDetailPage;
